import { useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscriptionStore, SubscriptionInterval } from '@/store/useSubscriptionStore';
import { subscribeToUserSubscription } from '@/lib/firestore';
import { useRoleAuth } from './useRoleAuth';

// Extended subscription interface for Firestore (includes legacy values)
interface FirestoreSubscription {
  plan: 'free' | 'premium';
  interval?: string; // Allow any string to handle legacy values
  purchasedAt?: string;
  expiresAt?: string;
  paymentId?: string;
  orderId?: string;
}

/**
 * Maps legacy interval values to current SubscriptionInterval type
 */
function mapInterval(interval: string | undefined): SubscriptionInterval {
  if (!interval) return 'quarterly';

  // Handle current values
  if (interval === 'quarterly') return 'quarterly';
  if (interval === 'halfyearly') return 'halfyearly';
  if (interval === 'yearly') return 'yearly';
  if (interval === 'lifetime') return 'lifetime';

  // Handle legacy values
  if (interval === 'monthly') return 'quarterly'; // Map monthly to quarterly

  // Default fallback
  return 'quarterly';
}

/**
 * Hook to verify subscription status from Firestore
 * This is the authoritative source of truth for subscription status
 */
export function useSubscriptionVerification() {
  const { user } = useAuth();
  const { setPlan, checkExpiry } = useSubscriptionStore();
  const { isAdmin, isModerator } = useRoleAuth();

  const verifySubscription = useCallback(async () => {
    if (!user) {
      setPlan('free');
      return;
    }

    // Admin and moderator always have premium
    if (isAdmin || isModerator) {
      setPlan('premium', 'lifetime', null);
      return;
    }

    // Check expiry on manual verification
    checkExpiry();
  }, [user, setPlan, checkExpiry, isAdmin, isModerator]);

  // Subscribe to real-time subscription updates from Firestore
  useEffect(() => {
    if (!user) {
      setPlan('free');
      return;
    }

    // Admin and moderator always have premium
    if (isAdmin || isModerator) {
      setPlan('premium', 'lifetime', null);
      return;
    }

    const unsubscribe = subscribeToUserSubscription(
      user.uid,
      (subscription) => {
        const sub = subscription as FirestoreSubscription | null;

        if (sub?.plan === 'premium') {
          // Check if subscription has expired
          if (sub.expiresAt && sub.interval !== 'lifetime') {
            const expiryDate = new Date(sub.expiresAt);
            const now = new Date();

            if (now > expiryDate) {
              // Subscription expired
              setPlan('free');
              return;
            }
          }

          // Map interval to valid SubscriptionInterval type
          const mappedInterval = mapInterval(sub.interval);

          setPlan(
            'premium',
            mappedInterval,
            sub.expiresAt || null
          );
        } else {
          setPlan('free');
        }
      },
      (error) => {
        console.error('Subscription listener error:', error);
        // On error, don't change current state
      }
    );

    return () => {
      unsubscribe();
    };
  }, [user, setPlan, isAdmin, isModerator]);

  // Check expiry periodically (every minute)
  useEffect(() => {
    const interval = setInterval(() => {
      checkExpiry();
    }, 60000);

    return () => clearInterval(interval);
  }, [checkExpiry]);

  return { verifySubscription };
}